import { useCallback } from 'react';
import { css } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { theme } from '@frontend/theme';
import { CheckboxField, Text, useFormField } from '@frontend/design-system';
import { useValidPhoneNumberFromInvoice } from '../../../hooks/use-valid-phone-number-from-invoice';
import { useValidPhoneNumberFromPerson } from '../../../hooks/use-valid-phone-number-from-person';
import { useCollectPaymentMultiContext } from '../../collect-payment-multi.context';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../utils/steps';
import { StepPerformanceWrapper } from '../atoms/step-performance-wrapper';
import { StyledStep } from '../atoms/styled-step';

export const CareCreditMessageDisclosure = () => {
  return (
    <StepPerformanceWrapper step={PAYMENT_MULTI_STEPS.careCreditMessageDisclosure}>
      <CareCreditMessageDisclosureBody />
    </StepPerformanceWrapper>
  );
};

const styles = {
  loader: css`
    height: 200px;
  `,
  body: css``,
  blocks: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    width: 100%;
  `,
  block: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  agreeBox: css`
    padding: ${theme.spacing(2)};
    background: ${theme.colors.neutral5};
    border: solid 1px ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.medium};
  `,
};

export const CareCreditMessageDisclosureBody = () => {
  const { t } = useTranslation('payments');

  const {
    invoice,
    person,
    careCreditMessageType,
    activeLocationId,
    multiStep: { onCancel, goNextStep, goPrevStep },
  } = useCollectPaymentMultiContext();

  const { firstValidPhoneNumber: firstInvoicePhoneNumber } = useValidPhoneNumberFromInvoice(invoice);

  /**
   * Thanks to the CareCredit payments panel, we might have a flow with no invoice.
   * So we needto have a person fallback, too. Internally, the CareCredit multi-steppers only do the valid SMS lookup
   * via person's id, rather than invoice.person.id, etc.
   */
  const { firstValidPhoneNumber: firstPersonPhoneNumber } = useValidPhoneNumberFromPerson(activeLocationId, person);

  const checkboxProps = useFormField({
    type: 'checkbox',
    required: true,
    value: false,
  });

  const phoneNumber = firstInvoicePhoneNumber || firstPersonPhoneNumber;

  const formattedPhoneNumber = formatPhoneNumber(phoneNumber || '');

  const isTransactionFlow = careCreditMessageType === 'payment-link';

  const onClickPrimary = useCallback(() => {
    goNextStep({
      orStep: PAYMENT_MULTI_STEPS.careCreditMessage,
    });
  }, [goNextStep]);

  const onClickSecondary = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const onClickBack = useCallback(() => {
    goPrevStep({
      orStep: PAYMENT_MULTI_STEPS.paymentMethods,
    });
  }, [goPrevStep]);

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.careCreditMessageDisclosure}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.careCreditMessageDisclosure]}
      hasInternalFooter
      css={styles.body}
      footer={{
        primary: {
          disabled: !checkboxProps.value || !phoneNumber,
          children: t('Next'),
        },
        secondary: {
          children: t('Cancel'),
        },
        onClickPrimary,
        onClickSecondary,
        onClickBack,

        /**
         * This step might be reached from the payments panel, where there is no invoice.
         * In which case, hide the back button, as there's no "payment methods" step to go back to.
         */
        showBackButton: !!invoice,
      }}
    >
      <div css={styles.blocks}>
        {isTransactionFlow ? (
          <Text>
            {t(
              'Before sending a secure CareCredit transaction link, you need your customer’s consent to receive texts.'
            )}
          </Text>
        ) : (
          <Text>
            {t(
              'Before sending a secure CareCredit application link, you need your customer’s consent to receive texts.'
            )}
          </Text>
        )}
        <div css={styles.block}>
          <Text weight='bold'>{t('Customer’s mobile number')}</Text>
          {!!formattedPhoneNumber ? (
            <Text>{formattedPhoneNumber}</Text>
          ) : (
            <Text color='error'>{t('No valid phone number')}</Text>
          )}
        </div>
        <div css={styles.block}>
          <Text weight='bold'>{t('Read to customer:')}</Text>
          <Text>
            {isTransactionFlow ? (
              <Trans>
                "We’ll send a one-time transaction link to the mobile number we have on file for you. Message and data
                rates may apply. For Terms & Conditions and Privacy Policy, visit Synchrony.com.
                <br />
                <br />
                If you understand and agree to what I have told you, please say YES."
              </Trans>
            ) : (
              <Trans>
                "We’ll send a one-time application link to the mobile number we have on file for you. Message and data
                rates may apply. For Terms & Conditions and Privacy Policy, visit Synchrony.com.
                <br />
                <br />
                If you understand and agree to what I have told you, please say YES."
              </Trans>
            )}
          </Text>
        </div>

        <div css={styles.agreeBox}>
          <CheckboxField {...checkboxProps} name='agree' label={t('Customer consents to receiving text.')} />
        </div>
      </div>
    </StyledStep>
  );
};
